import { darkScrollbar } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import {
  alpha,
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { type FC, type ReactNode } from "react";

const theme = createTheme({
  colorSchemes: {
    dark: {
      palette: {
        secondary: {
          main: "#FFFFFF",
          light: "#FFFFFF",
          dark: "#e4e4e7",
          contrastText: "#14151A",
        },
        yellow: {
          main: "#ab7d10",
          light: "#bc9740",
          dark: "#78580b",
          contrastText: "#14151A",
        },
        gold: {
          main: "#FFDE92",
          light: "#FFE5A8",
          dark: "#B29B66",
          contrastText: "#14151A",
        },
        grey: {
          "50": "#121212",
          "100": "#000",
        },
      },
    },
    light: {
      palette: {
        primary: {
          main: "#0B0C0E",
          dark: "#000000",
          light: "#2A2E35",
          contrastText: "#FFFFFF",
        },
        secondary: {
          main: "#f5f5f6",
          light: "#FFFFFF",
          dark: "#d0d0d2",
          contrastText: "#14151A",
        },
        yellow: {
          main: "#ffde92",
          light: "#ffe5a8",
          dark: "#b29b66",
          contrastText: "#14151A",
        },
        gold: {
          main: "#FFDE92",
          light: "#FFE5A8",
          dark: "#B29B66",
          contrastText: "#14151A",
        },
        success: {
          main: "#26BD6C",
          light: "#E9F8F0",
          dark: "#1E9E59",
          contrastText: "#0B3D22",
        },
      },
    },
  },
  typography: {
    fontFamily: "Inter, sans-serif",
    caption: {
      fontSize: 14,
      lineHeight: "142%",
      letterSpacing: "-0.1px",
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiTablePagination-select": {
            fontSize: "14px",
          },
          "& .MuiTablePagination-selectLabel": {
            fontSize: "14px",
          },
          "& .MuiTablePagination-displayedRows": {
            fontSize: "14px",
          },
          "& .MuiTablePagination-menuItem": {
            fontSize: "14px",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === "xs" && {
            width: 16,
            height: 10,
            padding: 0,
            overflow: "visible",

            "& .MuiSwitch-switchBase": {
              padding: 2,
              margin: 0,
              transitionDuration: "150ms",

              "&.Mui-checked": {
                transform: "translateX(6px)",
              },
            },

            "& .MuiSwitch-thumb": {
              width: 6,
              height: 6,
              boxShadow: "none",
            },

            "& .MuiSwitch-track": {
              width: 16,
              height: 10,
              borderRadius: 999,
              opacity: 1,
            },
          }),
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const paletteColor =
            ownerState.color &&
            ownerState.color !== "default" &&
            theme.palette[ownerState.color];

          return {
            border: "none",

            "&:not([data-skip-custom-styles='true']):not([data-custom-styles='true'])":
              {
                backgroundColor: paletteColor
                  ? alpha(paletteColor.main, 0.1)
                  : undefined,
              },
            "&[data-custom-styles='true']": {
              backgroundColor: paletteColor
                ? alpha(paletteColor.main, 0.5)
                : undefined,
            },
          };
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        body: themeParam.palette.mode === "dark" ? darkScrollbar() : null,
      }),
    },
  },
});

interface IThemeProviderProps {
  children: ReactNode;
  forcedMode?: "light" | "dark";
}

export const ThemeProvider: FC<IThemeProviderProps> = ({
  children,
  forcedMode,
}) => {
  return (
    <MuiThemeProvider
      theme={theme}
      defaultMode={forcedMode ?? "light"}
      storageManager={forcedMode ? null : undefined}
    >
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};
